// Semantic trigger coverage. Compares the description against a pre-computed
// canonical corpus of likely user phrasings (built offline from quality skills
// + the Anthropic guide). No model, no network. Uses bigram Jaccard similarity
// as a cheap proxy for semantic overlap.

import canonical from '../../../data/canonicalTriggers.json';

export interface SemanticMetrics {
  canonicalCount: number;
  matchedPhrases: string[];     // canonical phrases the description plausibly covers
  matchedCount: number;
  bestSimilarity: number;
  meanTopSimilarity: number;    // mean of top-5 similarities (signal density)
  hasDomainCoverage: boolean;   // ≥ 2 matched canonical phrases at score ≥ 0.4
}

const STOPWORDS = new Set([
  'the','a','an','of','to','for','with','and','or','in','on','at','by','from',
  'this','that','these','those','it','is','be','as','use','using','used','user',
  'users','when','if','so','not','do','does','did','your','their','them','they',
  'have','has','had','will','would','should','can','could','may','might','about',
  'into','onto','any','all','one','two','more','most','some','each','also','here',
  'there','then','than','what','which','who','how','why','where',
]);

function tokenize(s: string): string[] {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s'-]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOPWORDS.has(t));
}

function bigrams(tokens: string[]): Set<string> {
  const out = new Set<string>();
  for (let i = 0; i < tokens.length - 1; i++) {
    out.add(`${tokens[i]} ${tokens[i + 1]}`);
  }
  return out;
}

function jaccard<T>(a: Set<T>, b: Set<T>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const x of a) if (b.has(x)) intersection++;
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

// Containment: fraction of canonical's tokens that appear in description.
// Better than Jaccard for "short phrase vs long description" matching.
function containment<T>(canonical: Set<T>, doc: Set<T>): number {
  if (canonical.size === 0) return 0;
  let hit = 0;
  for (const x of canonical) if (doc.has(x)) hit++;
  return hit / canonical.size;
}

// Pre-tokenize the canonical phrases once at module load.
interface CanonicalEntry { phrase: string; tokens: Set<string>; bigrams: Set<string> }
const CANONICAL: CanonicalEntry[] = (canonical.phrases as string[]).map((phrase) => {
  const toks = tokenize(phrase);
  return { phrase, tokens: new Set(toks), bigrams: bigrams(toks) };
});

export function analyzeSemantic(description: string): SemanticMetrics {
  if (!description || description.length < 8) {
    return {
      canonicalCount: CANONICAL.length,
      matchedPhrases: [],
      matchedCount: 0,
      bestSimilarity: 0,
      meanTopSimilarity: 0,
      hasDomainCoverage: false,
    };
  }
  const tokens = tokenize(description);
  const tokenSet = new Set(tokens);
  const bigramSet = bigrams(tokens);

  const similarities: { phrase: string; sim: number }[] = [];
  for (const entry of CANONICAL) {
    // Composite: bigram containment dominates; unigram acts as floor.
    // Bigram containment ≥ 0.5 means the description carries the phrase's
    // verb+object structure.
    const uni = containment(entry.tokens, tokenSet);
    const bi = entry.bigrams.size === 0 ? uni : containment(entry.bigrams, bigramSet);
    const composite = uni * 0.3 + bi * 0.7;
    if (composite > 0.2) {
      similarities.push({ phrase: entry.phrase, sim: composite });
    }
  }
  similarities.sort((a, b) => b.sim - a.sim);

  const matched = similarities.filter((s) => s.sim >= 0.45);
  const top5 = similarities.slice(0, 5);
  const meanTop = top5.length === 0 ? 0 : top5.reduce((a, s) => a + s.sim, 0) / top5.length;

  return {
    canonicalCount: CANONICAL.length,
    matchedPhrases: matched.slice(0, 8).map((s) => s.phrase),
    matchedCount: matched.length,
    bestSimilarity: similarities[0]?.sim ?? 0,
    meanTopSimilarity: meanTop,
    hasDomainCoverage: matched.filter((m) => m.sim >= 0.4).length >= 2,
  };
}
